import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types';
import ExpansionPanel from "components/ExpansionPanel";
import AddThreadPopup from "./AddThreadPopup";
import Tr from "translations/Translations";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import AddThreadAsSupplierPopup from "./AddThreadAsSupplierPopup";
import GridBlock from "components/GridBlock";
import {makeStyles, Typography} from "@material-ui/core";
import Thread from "./Thread";
import AddBulkMessagePopup from "./AddBulkMessagePopup";
import FlagRoundedIcon from "@material-ui/icons/FlagRounded";
import OutlinedFlagRoundedIcon from "@material-ui/icons/OutlinedFlagRounded";
import TooltipIcon from "components/icon/TooltipIcon";
import CorrespondenceService from "services/CorrespondenceService";
import {connect} from "react-redux";
import SplitButton, {createButton as createSplitButton} from "components/popup/components/SplitButton";
import WorkableIconExpandButton from "components/button/WorkableIconExpandButton";
import PrivilegeService from "services/PrivilegeService";
import WorkableIconShareButton from "components/button/WorkableIconShareButton";
import useSnackbar from "hooks/useSnackbar";
import ShareSelectPopup, {sharePopupData} from "components/popup/ShareSelectPopup";


const useStyles = makeStyles(theme => ({
    root: {
        background: 'transparent'
    },
    summaryRoot: {
        '&>*:nth-child(2)': {
            flexGrow: 1,
            textAlign: 'center'
        }
    },
    details: {
        padding: 0
    },
    buttons: {
        padding: theme.spacing(4),
        marginBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.paper
    },
    buttonPrint: {
        width: 280,
        whiteSpace: "nowrap",
        overflow: "hidden"
    },
    redFlag: {
        color: theme.palette.error.main
    },
    shareButton: {
        marginBottom: -6,
        marginTop: -6,
    }
}));


function ThreadsPanel(
    {
        contractor,
        title,
        currentActive,
        rfxRound,
        listResourceId,
        threadsList,
        correspondencePrivileges,
        showShare
    }) {

    const classes = useStyles();

    const [addAsSupplierOpened, setAddAsSupplierOpened] = useState(false);
    const [addPopupOpened, setAddPopupOpened] = useState(false);
    const [bulkPopupOpened, setBulkPopupOpened] = useState(false);
    const [expanded, setExpanded] = useState(currentActive);
    const [selectedThreads, setSelectedThreads] = useState([]);
    const [printWorking, setPrintWorking] = useState(false);
    const [shareWorking, setShareWorking] = useState(false);
    const [shareFilePopup, setShareFilePopup] = useState(null);
    const {enqueueSuccessSnackbar} = useSnackbar();

    const hasEdit = useMemo(() => !!correspondencePrivileges.EDIT, [correspondencePrivileges]);

    const onThreadSelectChange = (thread, added) => {
        if (added) {
            setSelectedThreads(x => [...x, thread.threadId]);
        } else {
            setSelectedThreads(x => x.filter(x => x !== thread.threadId));
        }
    }


    const printCorrespondence = (type) => {
        if (printWorking) {
            return;
        }
        setPrintWorking(true);
        let printPromise;
        if (contractor) {
            printPromise = CorrespondenceService.getContractorPrint(rfxRound.id, type, listResourceId === CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, selectedThreads);
        } else {
            printPromise = CorrespondenceService.getBuyerPrint(rfxRound.id, type, listResourceId === CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, selectedThreads);
        }
        printPromise.finally(() => setPrintWorking(false))

    }

    const buttons = useMemo(() => {
        const buttons = [];
        const hasSelected = selectedThreads.length > 0;
        buttons.push(createButton(Tr("message_create_new"), () => setAddPopupOpened(true), undefined, !hasEdit || !currentActive));
        if (!contractor) {
            buttons.push(createButton(Tr("message_bulk_answer"), () => setBulkPopupOpened(true), 'outlined', !hasEdit || !currentActive || !hasSelected));
            buttons.push(createButton(Tr("message_create_as_contractor"), () => setAddAsSupplierOpened(true), 'outlined', !hasEdit || !currentActive));
        }

        return buttons;
    }, [hasEdit, currentActive, selectedThreads, contractor]);

    const hasRedFlag = useMemo(() => Boolean(threadsList.find(x => x.hasRedFlag)), [threadsList]);

    const getTitle = () => (
        <GridBlock alignItems="center" wrap='nowrap' justify='space-between' gridClasses={{root: classes.summaryRoot}} id={`section_${listResourceId}`}>

            <TooltipIcon className={classes.redFlag} Icon={hasRedFlag ? FlagRoundedIcon : OutlinedFlagRoundedIcon} fontSize='large'
                         tooltip={hasRedFlag ? Tr('correspondence_contains_flagged_threads') : Tr("correspondence_no_flagged_threads")}/>

            <Typography className={classes.subject} variant='h4'>
                {title}
            </Typography>
            <WorkableIconExpandButton
                color='primary'
                expanded={expanded}
            />
        </GridBlock>
    )

    const getPrintButton = useMemo(() => {
        const buttons = [];
        const hasSelected = selectedThreads.length > 0;
        buttons.push(createSplitButton(Tr(hasSelected ? "message_print_selected_pdf" : "message_print_pdf"), () => printCorrespondence("pdf"), !threadsList.length));
        buttons.push(createSplitButton(Tr(hasSelected ? "message_print_selected_rtf" : "message_print_rtf"), () => printCorrespondence("rtf"), !threadsList.length));
        return (<SplitButton
            label={Tr(hasSelected ? 'message_print_selected' : 'message_print')}
            working={printWorking}
            buttonClassName={classes.buttonPrint}
            disabled={!threadsList.length}
            buttons={buttons}
            variant="outlined"
            hideEmpty={false}
        />)
        // eslint-disable-next-line
    },[selectedThreads.length, threadsList.length, printWorking]);

    const onShareFile = (name, selectedThreads) => setShareFilePopup(sharePopupData(name, selectedThreads));

    const getShareButton = useMemo(()=> {
        if(showShare) {
            const hasSelected = selectedThreads.length > 0;

            return <WorkableIconShareButton
                    className={classes.shareButton}
                    tooltip={Tr(hasSelected ? "message_share_selected_pdf" : "message_share_pdf")}
                    onClick={()=> onShareFile(Tr(hasSelected ? "message_share_selected_pdf" : "message_share_pdf"), {selectedThreads, type: "pdf"})}
                    working={shareWorking}
                    disabled={shareWorking}
                />
        }
        return undefined;
        // eslint-disable-next-line
    }, [selectedThreads.length, threadsList.length, showShare, shareWorking])


    const doShare = ({selectedThreads, type}, system) => {
        setShareFilePopup(null);
        setShareWorking(true);
        CorrespondenceService.shareCorrespondencePrint(rfxRound.id, type, listResourceId === CorrespondenceService.CORRESPONDENCE_THREAD_LISTS_BEFORE_END_DATE_RESOURCE_ID, selectedThreads, system).then((resp) => {
            if (resp)
                enqueueSuccessSnackbar(Tr("int_upload_success_x", {x: system}, true))
        }).finally(() => {
            setShareWorking(false);
        });
    }

    return (
        <>
            <ExpansionPanel titleAdornment={getTitle()} expanded={expanded} onChange={(event, expanded) => {
                setExpanded(expanded)
            }} square classesObj={{root: classes.root, details: classes.details}}>
                <GridBlock column>
                    <MultiButton buttons={buttons} justify="space-evenly" className={classes.buttons} after={getPrintButton} afterAfter={getShareButton}/>
                    {threadsList.map((el) => (
                        <Thread key={el.threadId} thread={el} rfxRoundId={rfxRound.id} onThreadSelectChange={onThreadSelectChange}
                                selectedThreads={selectedThreads} listResourceId={listResourceId} contractor={contractor} currentActive={currentActive}/>
                    ))}
                </GridBlock>
            </ExpansionPanel>

            {(hasEdit && !contractor && addAsSupplierOpened) &&
            <AddThreadAsSupplierPopup onClose={() => setAddAsSupplierOpened(false)} rfxRoundId={rfxRound.id} listResourceId={listResourceId}/>}
            {hasEdit && addPopupOpened &&
            <AddThreadPopup onClose={() => setAddPopupOpened(false)} rfxRoundId={rfxRound.id} listResourceId={listResourceId} contractor={contractor} />}
            {(hasEdit && !contractor && bulkPopupOpened) &&
            <AddBulkMessagePopup rfxRoundId={rfxRound.id} onClose={() => setBulkPopupOpened(false)} listResourceId={listResourceId}
                                 selectedThreads={selectedThreads} threadsList={threadsList}/>}
            {!!shareFilePopup && <ShareSelectPopup
                data={shareFilePopup}
                onCancel={() => setShareFilePopup(null)}
                onChoose={doShare}
            />}
        </>
    )
}

ThreadsPanel.propTypes = {
    classes: PropTypes.any,
}

const mapStateToProps = (state) => {
    return {
        correspondencePrivileges: state.privileges[PrivilegeService.CORRESPONDENCE_PRIVILEGE_STORE_KEY],
    }
}
const mapDispatchToProps = {}

export default connect(mapStateToProps, mapDispatchToProps)(ThreadsPanel);